/**
 * As referências normativas do trabalho em altura, num arquivo de dados: o
 * instrutor corrige um item aqui sem mexer em mais nada. Não há texto de norma
 * no app, só o assunto em palavras nossas e onde ele está na norma.
 *
 * NENHUMA referência abaixo foi conferida no texto oficial (`conferido: false`):
 * na preparação desta fase, as páginas do gov.br não puderam ser abertas. A
 * NR-35 foi reescrita pela Portaria MTP nº 4.218/2022 e mudou de numeração, e
 * fontes secundárias ainda circulam com a redação de 2012. Por isso o número do
 * item fica `null` até alguém conferir; `ondeProcurar` é só a pista para isso.
 * Ao conferir, preencha `item`, `versao` e `dataConsulta` e marque `conferido`.
 */

export const IDS_REFERENCIA_NORMATIVA = [
  'nr35-aplicacao',
  'nr35-capacitacao',
  'nr35-analise-de-risco',
  'nr35-permissao-de-trabalho',
  'nr35-protecao-contra-quedas',
  'nr35-ancoragem',
  'nr35-emergencia-e-salvamento',
  'nr6-epi',
] as const
export type IdReferenciaNormativa = (typeof IDS_REFERENCIA_NORMATIVA)[number]

export interface ReferenciaNormativa {
  readonly id: IdReferenciaNormativa
  readonly norma: string
  /** O que a referência cobre, em palavras nossas (não é o texto da norma). */
  readonly assunto: string
  /** Número do item no texto oficial vigente; `null` enquanto não foi conferido. */
  readonly item: string | null
  /** Pista de onde procurar, para quem for conferir. Vem de fontes secundárias. */
  readonly ondeProcurar: string
  /** A redação a que a referência se refere. */
  readonly versao: string
  /** Onde está o texto oficial. */
  readonly fonte: string
  /** Data em que o item foi conferido na fonte (AAAA-MM-DD); `null` se nunca foi. */
  readonly dataConsulta: string | null
  /** `true` só depois de conferido no texto oficial e validado pelo instrutor. */
  readonly conferido: boolean
}

const FONTE_NR35 =
  'https://www.gov.br/trabalho-e-emprego/pt-br/acesso-a-informacao/participacao-social/conselhos-e-orgaos-colegiados/comissao-tripartite-partitaria-permanente/normas-regulamentadora/normas-regulamentadoras-vigentes/nr-35-atualizada-2025-1.pdf'
const FONTE_NRS_VIGENTES =
  'https://www.gov.br/trabalho-e-emprego/pt-br/acesso-a-informacao/participacao-social/conselhos-e-orgaos-colegiados/comissao-tripartite-partitaria-permanente/normas-regulamentadora/normas-regulamentadoras-vigentes'
const VERSAO_NR35 = 'Redação da Portaria MTP nº 4.218/2022, com as alterações posteriores (a conferir qual é a vigente)'

export const REFERENCIAS_NORMATIVAS: Readonly<Record<IdReferenciaNormativa, ReferenciaNormativa>> = {
  'nr35-aplicacao': {
    id: 'nr35-aplicacao',
    norma: 'NR-35',
    assunto: 'Quando a norma se aplica: trabalho acima de 2 m do nível inferior, com risco de queda.',
    item: null,
    ondeProcurar: 'Capítulo de objetivo e campo de aplicação, no início da norma.',
    versao: VERSAO_NR35,
    fonte: FONTE_NR35,
    dataConsulta: null,
    conferido: false,
  },
  'nr35-capacitacao': {
    id: 'nr35-capacitacao',
    norma: 'NR-35',
    assunto: 'Capacitação, autorização e aptidão do trabalhador para o trabalho em altura.',
    item: null,
    ondeProcurar: 'Na redação de 2022, o capítulo de autorização, capacitação e aptidão (35.4, segundo fontes secundárias).',
    versao: VERSAO_NR35,
    fonte: FONTE_NR35,
    dataConsulta: null,
    conferido: false,
  },
  'nr35-analise-de-risco': {
    id: 'nr35-analise-de-risco',
    norma: 'NR-35',
    assunto: 'Análise de risco antes do trabalho: riscos da atividade, do local e do entorno, isolamento e sinalização da área.',
    item: null,
    ondeProcurar: 'Na redação de 2022, o capítulo de planejamento e organização (35.5; a análise de risco aparece como 35.5.5 em fontes secundárias).',
    versao: VERSAO_NR35,
    fonte: FONTE_NR35,
    dataConsulta: null,
    conferido: false,
  },
  'nr35-permissao-de-trabalho': {
    id: 'nr35-permissao-de-trabalho',
    norma: 'NR-35',
    assunto: 'Permissão de trabalho para a atividade não rotineira: emitida antes, aprovada pelo responsável, disponível no local e arquivada.',
    item: null,
    ondeProcurar: 'Na redação de 2022, o mesmo capítulo de planejamento e organização da análise de risco.',
    versao: VERSAO_NR35,
    fonte: FONTE_NR35,
    dataConsulta: null,
    conferido: false,
  },
  'nr35-protecao-contra-quedas': {
    id: 'nr35-protecao-contra-quedas',
    norma: 'NR-35',
    assunto: 'Sistemas de proteção contra quedas: escolha do sistema, zona livre de queda, fator de queda e inspeção dos equipamentos.',
    item: null,
    ondeProcurar: 'O capítulo de sistemas de proteção contra quedas.',
    versao: VERSAO_NR35,
    fonte: FONTE_NR35,
    dataConsulta: null,
    conferido: false,
  },
  'nr35-ancoragem': {
    id: 'nr35-ancoragem',
    norma: 'NR-35',
    assunto: 'Sistemas de ancoragem: onde e como prender o sistema de proteção.',
    item: null,
    ondeProcurar: 'O anexo de sistemas de ancoragem (Anexo II na redação de 2022, segundo fontes secundárias).',
    versao: VERSAO_NR35,
    fonte: FONTE_NR35,
    dataConsulta: null,
    conferido: false,
  },
  'nr35-emergencia-e-salvamento': {
    id: 'nr35-emergencia-e-salvamento',
    norma: 'NR-35',
    assunto: 'Emergência e salvamento: equipe, recursos e procedimento de resgate previstos antes de o trabalho começar.',
    item: null,
    ondeProcurar: 'O capítulo de emergência e salvamento.',
    versao: VERSAO_NR35,
    fonte: FONTE_NR35,
    dataConsulta: null,
    conferido: false,
  },
  'nr6-epi': {
    id: 'nr6-epi',
    norma: 'NR-6',
    assunto: 'Equipamento de proteção individual: certificado de aprovação, conservação e verificação antes do uso.',
    item: null,
    ondeProcurar: 'As responsabilidades do empregador e do trabalhador quanto ao EPI.',
    versao: 'Texto vigente (a conferir)',
    fonte: FONTE_NRS_VIGENTES,
    dataConsulta: null,
    conferido: false,
  },
}

export const HA_NORMA_NAO_CONFERIDA = Object.values(REFERENCIAS_NORMATIVAS).some((r) => !r.conferido)

export const AVISO_NORMAS_NAO_CONFERIDAS =
  'Referências não conferidas: os itens das normas ainda precisam ser conferidos no texto oficial vigente e validados pelo instrutor. Os números dos itens ficam em branco até lá.'
